// Squad H2H setup — your team name + color, optional friends, opponent
// (by friend code redeem flow — for now a paste-the-opponent-squad-id input),
// and route choice (freestyle | bar crawl). Ports canon SquadSetupScreen.
//
// Per chat: "design squad mode think of it as teams head v head, u set a
// route (bar crawl) or undetermined." This screen creates the user's squad
// + battle in one tap of "Start battle".

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useEffect, useState } from 'react'
import {
  type BattleRouteType,
  type FriendRequestDto,
  type SquadColor,
  type SquadDto,
  type VenueDto,
  authApi,
  friendsApi,
  squadsApi,
  venuesApi,
} from '../../api'
import { Icon } from '../../components/Icon'
import { BackButton, TopBar } from '../../components/TopBar'

export const Route = createFileRoute('/squad/setup')({
  component: SquadSetupRoute,
})

const COLORS: { id: SquadColor; var: string }[] = [
  { id: 'pink', var: 'var(--pink)' },
  { id: 'lime', var: 'var(--lime)' },
  { id: 'teal', var: 'var(--teal)' },
  { id: 'orange', var: 'var(--orange)' },
]

function fgFor(color: SquadColor): string {
  return color === 'pink' || color === 'orange' ? 'var(--paper)' : 'var(--ink)'
}

type AddedFriend = { id: string; displayName: string; letter: string; color: string }

function SquadSetupRoute() {
  const navigate = useNavigate()
  const [authStatus, setAuthStatus] = useState<'loading' | 'anon' | 'ok'>('loading')

  // Your team
  const [teamName, setTeamName] = useState('')
  const [teamColor, setTeamColor] = useState<SquadColor>('pink')
  const [addedFriends, setAddedFriends] = useState<AddedFriend[]>([])

  // Friend picker
  const [pickerOpen, setPickerOpen] = useState(false)
  const [friends, setFriends] = useState<FriendRequestDto[]>([])

  // Opponent
  const [opponentSquadId, setOpponentSquadId] = useState('')

  // Route
  const [routeMode, setRouteMode] = useState<BattleRouteType>('freestyle')

  // Crawl: 4 venues, drag-to-reorder is overkill for MVP — we add via search.
  const [venueQuery, setVenueQuery] = useState('')
  const [venueResults, setVenueResults] = useState<VenueDto[]>([])
  const [crawlVenues, setCrawlVenues] = useState<VenueDto[]>([])

  // Submit
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      const u = await authApi.me()
      if (!u) {
        setAuthStatus('anon')
        navigate({ to: '/login' })
        return
      }
      setAuthStatus('ok')
    })()
  }, [navigate])

  useEffect(() => {
    if (!pickerOpen) return
    void (async () => {
      const { incoming, outgoing } = await friendsApi.requests()
      // We only have requests endpoints — accepted friendships surface here
      // when status === 'accepted'. Filter to the accepted list (server-side
      // would be nicer; this is a placeholder consistent with /your-people).
      const accepted = [...incoming, ...outgoing].filter((r) => r.status === 'accepted')
      setFriends(accepted)
    })()
  }, [pickerOpen])

  useEffect(() => {
    const q = venueQuery.trim()
    if (q.length < 2 || routeMode !== 'crawl') {
      setVenueResults([])
      return
    }
    let cancelled = false
    void (async () => {
      const results = await venuesApi.search(q)
      if (!cancelled) setVenueResults(results.slice(0, 8))
    })()
    return () => {
      cancelled = true
    }
  }, [venueQuery, routeMode])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const name = teamName.trim()
    if (name.length === 0) {
      setError('Pick a team name first.')
      return
    }
    if (opponentSquadId.trim().length === 0) {
      setError('Paste the rival squad id (share link coming soon).')
      return
    }

    setBusy(true)
    try {
      const created = await squadsApi.create({ name, color: teamColor })

      // Add friends as members (best-effort — silently skip failures).
      for (const f of addedFriends) {
        try {
          await squadsApi.addMember(created.squad.id, f.id)
        } catch {
          /* ignore — keep going */
        }
      }

      const venuesArg =
        routeMode === 'crawl'
          ? crawlVenues.map((v, i) => ({ venueId: v.id, orderIdx: i }))
          : undefined

      const { battle } = await squadsApi.startBattle(created.squad.id, {
        opponentSquadId: opponentSquadId.trim(),
        routeType: routeMode,
        venues: venuesArg,
      })

      navigate({ to: '/squad/battle/$id', params: { id: battle.id } })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
    } finally {
      setBusy(false)
    }
  }

  function addCrawlVenue(v: VenueDto) {
    if (crawlVenues.length >= 4) return
    if (crawlVenues.some((x) => x.id === v.id)) return
    setCrawlVenues([...crawlVenues, v])
    setVenueQuery('')
    setVenueResults([])
  }

  function removeCrawlVenue(id: string) {
    setCrawlVenues(crawlVenues.filter((v) => v.id !== id))
  }

  if (authStatus !== 'ok') return null

  const teamColorVar = COLORS.find((c) => c.id === teamColor)?.var ?? 'var(--pink)'
  const fg = fgFor(teamColor)

  return (
    <div className="stumble-screen" data-testid="squad-setup-screen">
      <TopBar
        left={<BackButton onClick={() => navigate({ to: '/' })} />}
        eyebrow="squad mode"
        title="team setup"
      />
      <form
        className="screen-body"
        onSubmit={handleSubmit}
        style={{
          gridTemplateRows: 'auto auto auto auto auto 1fr auto',
          gap: 12,
          paddingBottom: 'max(16px, calc(var(--safe-bottom, env(safe-area-inset-bottom)) + 16px))',
        }}
      >
        {/* YOUR TEAM card */}
        <div
          className="card"
          style={{
            background: teamColorVar,
            color: fg,
            display: 'grid',
            gap: 10,
          }}
        >
          <div className="row-between">
            <span className="eyebrow" style={{ color: fg }}>
              your team
            </span>
            <span
              className="chip"
              style={{ background: 'var(--paper)', color: 'var(--ink)' }}
              data-testid="squad-member-count"
            >
              {addedFriends.length + 1} ppl
            </span>
          </div>

          <input
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            placeholder="team name"
            aria-label="Team name"
            data-testid="squad-team-name"
            style={{
              minHeight: 48,
              padding: '0 12px',
              border: '2px solid var(--line)',
              borderRadius: 10,
              fontFamily: 'var(--font-display)',
              fontSize: 24,
              background: 'var(--paper)',
              color: 'var(--ink)',
              width: '100%',
            }}
          />

          <div className="row" style={{ gap: 8, justifyContent: 'space-between' }}>
            {COLORS.map((c) => (
              <button
                type="button"
                key={c.id}
                onClick={() => setTeamColor(c.id)}
                aria-label={`Color ${c.id}`}
                aria-pressed={teamColor === c.id}
                data-testid={`squad-color-${c.id}`}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 999,
                  border: '2.5px solid var(--line)',
                  background: c.var,
                  boxShadow:
                    teamColor === c.id
                      ? `0 0 0 3px var(--ink) inset, 0 0 0 5px ${c.var} inset`
                      : '0 3px 0 var(--shadow-ink)',
                }}
              />
            ))}
          </div>
        </div>

        {/* MEMBERS */}
        <div className="stack-sm">
          <span className="eyebrow">members</span>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {addedFriends.map((f) => (
              <span
                key={f.id}
                className="chip"
                style={{ background: f.color, color: 'var(--ink)', minHeight: 36 }}
              >
                {f.letter} · {f.displayName}
              </span>
            ))}
            <button
              type="button"
              onClick={() => setPickerOpen(!pickerOpen)}
              data-testid="squad-add-friend"
              style={{
                minHeight: 36,
                padding: '0 12px',
                border: '2px solid var(--line)',
                borderRadius: 999,
                background: 'var(--paper)',
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              + friends
            </button>
          </div>
          {pickerOpen ? (
            <div
              style={{
                border: '2px solid var(--line)',
                borderRadius: 12,
                padding: 8,
                background: 'var(--paper)',
                maxHeight: 160,
                overflowY: 'auto',
              }}
            >
              {friends.length === 0 ? (
                <p className="body-sm muted" style={{ margin: 6 }}>
                  No friends yet. Add some at /your-people first.
                </p>
              ) : (
                friends.map((req) => {
                  // The "friend" is whichever side of the request isn't us —
                  // we don't have `me` here, but both sides surface as PublicUserDto
                  // so just show both names; the simpler approach is to display the
                  // addressee (current user is the requester or vice versa).
                  const user = addedFriends.some((af) => af.id === req.requester.id)
                    ? req.addressee
                    : req.requester
                  return (
                    <button
                      type="button"
                      key={req.id}
                      onClick={() => {
                        if (addedFriends.some((af) => af.id === user.id)) return
                        const letter = (user.displayName ?? '?').charAt(0).toUpperCase()
                        const colorVar =
                          ['var(--lime)', 'var(--teal)', 'var(--orange)', 'var(--plum)'][
                            addedFriends.length % 4
                          ] ?? 'var(--lime)'
                        setAddedFriends([
                          ...addedFriends,
                          { id: user.id, displayName: user.displayName, letter, color: colorVar },
                        ])
                      }}
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                        width: '100%',
                        padding: 8,
                        border: 'none',
                        background: 'transparent',
                        textAlign: 'left',
                        minHeight: 44,
                      }}
                    >
                      <span
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 999,
                          background: 'var(--lime)',
                          display: 'grid',
                          placeItems: 'center',
                          fontFamily: 'var(--font-display)',
                        }}
                      >
                        {(user.displayName ?? '?').charAt(0).toUpperCase()}
                      </span>
                      <span style={{ fontWeight: 700 }}>{user.displayName}</span>
                    </button>
                  )
                })
              )}
            </div>
          ) : null}
        </div>

        {/* ROUTE choice */}
        <div className="stack-sm">
          <span className="eyebrow">route</span>
          <div className="row" style={{ gap: 10 }}>
            <button
              type="button"
              onClick={() => setRouteMode('freestyle')}
              aria-pressed={routeMode === 'freestyle'}
              data-testid="squad-route-freestyle"
              style={{
                flex: 1,
                minHeight: 70,
                padding: 12,
                border: '2.5px solid var(--line)',
                borderRadius: 14,
                background: routeMode === 'freestyle' ? 'var(--ink)' : 'var(--paper)',
                color: routeMode === 'freestyle' ? 'var(--paper)' : 'var(--ink)',
                boxShadow: '0 5px 0 var(--shadow-ink)',
                textAlign: 'left',
              }}
            >
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 22 }}>freestyle</div>
              <div className="body-sm" style={{ opacity: 0.85, marginTop: 2 }}>
                Anywhere you check in counts.
              </div>
            </button>
            <button
              type="button"
              onClick={() => setRouteMode('crawl')}
              aria-pressed={routeMode === 'crawl'}
              data-testid="squad-route-crawl"
              style={{
                flex: 1,
                minHeight: 70,
                padding: 12,
                border: '2.5px solid var(--line)',
                borderRadius: 14,
                background: routeMode === 'crawl' ? 'var(--ink)' : 'var(--paper)',
                color: routeMode === 'crawl' ? 'var(--paper)' : 'var(--ink)',
                boxShadow: '0 5px 0 var(--shadow-ink)',
                textAlign: 'left',
              }}
            >
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 22 }}>bar crawl</div>
              <div className="body-sm" style={{ opacity: 0.85, marginTop: 2 }}>
                4 stops · same bars, both teams.
              </div>
            </button>
          </div>

          {routeMode === 'crawl' ? (
            <div
              className="stack-sm"
              style={{
                border: '2px solid var(--line)',
                borderRadius: 12,
                padding: 10,
                background: 'var(--paper)',
              }}
            >
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                {crawlVenues.map((v, i) => (
                  <span
                    key={v.id}
                    className="chip"
                    style={{
                      background: 'var(--teal)',
                      color: 'var(--ink)',
                      minHeight: 36,
                      display: 'inline-flex',
                      gap: 6,
                      alignItems: 'center',
                    }}
                  >
                    {i + 1}. {v.name}
                    <button
                      type="button"
                      onClick={() => removeCrawlVenue(v.id)}
                      style={{
                        marginLeft: 4,
                        background: 'transparent',
                        border: 'none',
                        fontWeight: 900,
                      }}
                      aria-label={`Remove ${v.name}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
                {crawlVenues.length === 0 ? (
                  <span className="body-sm muted">Add up to 4 stops.</span>
                ) : null}
              </div>
              {crawlVenues.length < 4 ? (
                <>
                  <input
                    value={venueQuery}
                    onChange={(e) => setVenueQuery(e.target.value)}
                    placeholder="search bars to add"
                    aria-label="Search venues"
                    style={{
                      minHeight: 44,
                      padding: '0 12px',
                      border: '2px solid var(--line)',
                      borderRadius: 10,
                      fontSize: 16,
                      background: 'var(--paper)',
                    }}
                  />
                  {venueResults.length > 0 ? (
                    <div style={{ display: 'grid', gap: 4 }}>
                      {venueResults.map((v) => (
                        <button
                          type="button"
                          key={v.id}
                          onClick={() => addCrawlVenue(v)}
                          style={{
                            padding: 8,
                            border: '1.5px dashed var(--line)',
                            borderRadius: 8,
                            background: 'var(--paper-warm)',
                            textAlign: 'left',
                            minHeight: 44,
                            fontWeight: 600,
                          }}
                        >
                          + {v.name}{' '}
                          <span className="muted" style={{ fontWeight: 400 }}>
                            {v.city ?? ''}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* OPPONENT */}
        <div className="stack-sm">
          <span className="eyebrow">opponent</span>
          <input
            value={opponentSquadId}
            onChange={(e) => setOpponentSquadId(e.target.value)}
            placeholder="paste rival squad id (share link soon)"
            aria-label="Opponent squad id"
            data-testid="squad-opponent-id"
            style={{
              minHeight: 48,
              padding: '0 12px',
              border: '2.5px solid var(--line)',
              borderRadius: 12,
              background: 'var(--paper)',
              fontSize: 16,
              width: '100%',
            }}
          />
          <p className="body-sm muted">
            Both squads exist as separate teams. After you create yours, share its id with the other
            team to start the battle.
          </p>
        </div>

        {/* spacer */}
        <div />

        {/* Submit */}
        <div className="stack-sm" style={{ gap: 6 }}>
          {error ? (
            <p className="body-sm" style={{ color: 'var(--pink)', margin: 0 }} role="alert">
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            className="btn pink full"
            disabled={busy}
            data-testid="squad-submit"
            style={{ minHeight: 56 }}
          >
            {busy ? 'Starting…' : 'Start battle ⚔︎'}
          </button>
        </div>
      </form>
    </div>
  )
}
